import { calculateTotalSpent, calculateFullSupplierSpending } from '../../utils/orderHistoryCalculations';
import { calculateTotalRevenue } from '../../utils/revenueHistoryCalculations';
import { calculateCostPercentage } from '../../utils/kpiCalculations';
import { calculateCategorySpentBreakdown, calculateSpendingSplit } from '../../utils/chartsUtils';
import { hasMultipleRestaurants, isViewingAllRestaurants } from './tourRestaurant';
import {
  formatEuro,
  formatEuroShort,
  formatPct,
  readTourSnapshot,
  sideFromSplitName,
  sideLabel,
} from './tourValues';

/**
 * Live order-history numbers for the tour copy. See `tourValues.ts` for how `context`
 * decides which variant of a sentence is allowed to run.
 */

/** Spend and cost percentage for the selected timeframe, without period comparisons. */
export function getOrdersKpiValues(): Record<string, string> {
  const snapshot = readTourSnapshot('orders');
  const totalSpent = calculateTotalSpent(snapshot.orders, snapshot.showExVatOrders);
  if (totalSpent === 0) return { context: '' };

  const spent = formatEuro(totalSpent);
  const totalRevenue = calculateTotalRevenue(snapshot.revenue, snapshot.showExVatRevenue);
  if (totalRevenue === 0) return { context: 'spend', spent };

  const costPercent = formatPct(calculateCostPercentage(totalSpent, totalRevenue));
  return { context: 'cost', spent, costPercent };
}

/** Describe the selected view without changing the user's restaurant. */
export function getOrdersFullPictureValues(): Record<string, string> {
  return { context: hasMultipleRestaurants() && isViewingAllRestaurants() ? 'group' : '' };
}

/** A lead worth calling "well ahead of" rather than merely "ahead of". */
const CLEAR_LEAD = 1.25;

/**
 * Spend by supplier. `context`:
 *  - ahead / close — two or more suppliers, and whether the top one is clearly in front
 *  - single        — every euro in this view went to one supplier
 *  - ''            — no supplier spend in this view
 */
export function getOrdersSupplierValues(): Record<string, string> {
  const snapshot = readTourSnapshot('orders');
  const suppliers = calculateFullSupplierSpending(snapshot.orders, snapshot.showExVatOrders)
    .filter(entry => entry.value > 0);
  if (suppliers.length === 0) return { context: '' };

  const topAmount = formatEuroShort(suppliers[0].value);
  if (suppliers.length === 1) return { context: 'single', topAmount };

  return {
    context: suppliers[0].value >= suppliers[1].value * CLEAR_LEAD ? 'ahead' : 'close',
    topAmount,
    nextAmount: formatEuroShort(suppliers[1].value),
  };
}

/**
 * Spend by product category, ranked as the chart ranks it. `context`:
 *  - two / one — the largest positive categories to name
 *  - ''                — no categorised spend in this view
 */
export function getOrdersCategoryValues(): Record<string, string> {
  const snapshot = readTourSnapshot('orders');
  const categories = calculateCategorySpentBreakdown(snapshot.orders, snapshot.showExVatOrders)
    .filter(entry => entry.amount > 0);
  if (categories.length === 0) return { context: '' };

  const cat1 = categories[0].category;
  const pct1 = formatPct(categories[0].percentage);
  if (categories.length === 1) return { context: 'one', cat1, pct1 };

  const base = {
    cat1,
    pct1,
    cat2: categories[1].category,
    pct2: formatPct(categories[1].percentage),
  };
  return { context: 'two', ...base };
}

/**
 * Food / drinks / non-food split, using the same slices the bar draws. `context`:
 *  - three / two / one — how many sides carry spend
 *  - ''                — nothing to split
 */
export function getOrdersSplitValues(): Record<string, string> {
  const snapshot = readTourSnapshot('orders');
  const splitFilter = snapshot.overallTypeFilter === 'food'
    ? 'food'
    : snapshot.overallTypeFilter === 'drinks' ? 'drinks' : 'all';
  const slices = calculateSpendingSplit(snapshot.orders, splitFilter, snapshot.showExVatOrders)
    .filter(slice => slice.value > 0)
    .sort((a, b) => b.value - a.value);
  if (slices.length === 0) return { context: '' };

  const first = sideLabel(sideFromSplitName(slices[0].name));
  const firstPct = formatPct(slices[0].percentage);
  if (slices.length === 1) return { context: 'one', first, firstPct };

  const base = {
    first,
    firstPct,
    second: sideLabel(sideFromSplitName(slices[1].name)),
    secondPct: formatPct(slices[1].percentage),
  };
  if (slices.length === 2) return { context: 'two', ...base };

  return {
    context: 'three',
    ...base,
    third: sideLabel(sideFromSplitName(slices[2].name)),
    thirdPct: formatPct(slices[2].percentage),
  };
}
